// Self-contained: also embedded in the isolated Word and Spreadsheet documents.
export function createDrawingCore(options = {}) {
    const kinds = ['pen', 'highlighter', 'line', 'arrow', 'rectangle', 'ellipse', 'triangle'];
    const maxPoints = Math.max(12000,Math.min(300000,Number(options.maxPoints)||12000)), maxStrokes = Math.max(1200,Math.min(30000,Number(options.maxStrokes)||1200));
    const maxStrokePoints=Math.max(2000,Math.min(10000,Number(options.maxStrokePoints)||2000)),maxHeight=Math.max(60000,Math.min(300000,Number(options.maxHeight)||60000));
    const bound = (n, min, max, fallback = min) => Number.isFinite(Number(n)) ? Math.max(min, Math.min(max, Number(n))) : fallback;
    const round = n => Math.round(n * 10) / 10;
    function normalize(input) {
        let remaining = maxPoints;
        const strokes = [];
        for (const item of (Array.isArray(input?.strokes) ? input.strokes : []).slice(0, maxStrokes)) {
            if (!item || !kinds.includes(item.kind)) continue;
            let source = item.points;
            if (typeof source === 'string' && source.length <= maxStrokePoints*40) { try { source = JSON.parse(source); } catch { source = []; } }
            if (!Array.isArray(source)) continue;
            const points = source.slice(0, Math.min(remaining, maxStrokePoints)).filter(p => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1])).map(p => [round(bound(p[0], 0, 20000)), round(bound(p[1], 0, maxHeight))]);
            if (!points.length) continue;
            remaining -= points.length;
            strokes.push({kind: item.kind, color: /^#[0-9a-f]{6}$/i.test(item.color) ? item.color : '#7c3aed', width: bound(item.width, 1, 24, 3), points});
        }
        return {version: 1, height: bound(input?.height, 0, maxHeight), strokes};
    }
    // Simplify only sub-pixel variation, retaining endpoints and corners.
    function simplify(points, tolerance = .65) {
        if(points.length<3)return points;
        const keep=new Set([0,points.length-1]),stack=[[0,points.length-1]],threshold=tolerance*tolerance;
        while(stack.length){
            const range=stack.pop(),start=range[0],end=range[1],a=points[start],b=points[end],dx=b[0]-a[0],dy=b[1]-a[1],den=dx*dx+dy*dy;
            let far=threshold,index=-1;
            for(let i=start+1;i<end;i++){
                const p=points[i],t=den?Math.max(0,Math.min(1,((p[0]-a[0])*dx+(p[1]-a[1])*dy)/den)):0;
                const distance=(p[0]-a[0]-t*dx)**2+(p[1]-a[1]-t*dy)**2;
                if(distance>far){far=distance;index=i;}
            }
            if(index>=0){keep.add(index);stack.push([start,index],[index,end]);}
        }
        return points.filter((_,index)=>keep.has(index));
    }
    function path(stroke) {
        const p = stroke.points, a = p[0], b = p[p.length - 1];
        if (!a) return '';
        const x = Math.min(a[0], b[0]), y = Math.min(a[1], b[1]), w = Math.abs(b[0] - a[0]), h = Math.abs(b[1] - a[1]);
        if (stroke.kind === 'rectangle') return `M${x},${y}h${w}v${h}h${-w}Z`;
        if (stroke.kind === 'triangle') return `M${x + w / 2},${y}L${x + w},${y + h}L${x},${y + h}Z`;
        if (stroke.kind === 'ellipse') return `M${x},${y + h / 2}a${w / 2},${h / 2} 0 1,0 ${w},0a${w / 2},${h / 2} 0 1,0 ${-w},0`;
        if (stroke.kind === 'arrow') {
            const angle = Math.atan2(b[1] - a[1], b[0] - a[0]), head = Math.max(12, stroke.width * 4);
            return `M${a}L${b}M${b[0] - head * Math.cos(angle - .5)},${b[1] - head * Math.sin(angle - .5)}L${b}L${b[0] - head * Math.cos(angle + .5)},${b[1] - head * Math.sin(angle + .5)}`;
        }
        if (stroke.kind === 'line') return `M${a}L${b}`;
        if (p.length === 1) return `M${a}l.1,0`;
        let d = `M${a}`;
        for (let i = 1; i < p.length - 1; i++) d += `Q${p[i]} ${round((p[i][0] + p[i + 1][0]) / 2)},${round((p[i][1] + p[i + 1][1]) / 2)}`;
        return d + `L${b}`;
    }
    function bounds(data) {
        let width = 1, height = data.height || 1;
        data.strokes.forEach(s => s.points.forEach(p => { width = Math.max(width, p[0] + s.width + 16); height = Math.max(height, p[1] + s.width + 16); }));
        return {width: Math.ceil(width), height: Math.ceil(height)};
    }
    function paths(data) {
        return data.strokes.map(s => `<path d="${path(s)}" fill="none" stroke="${s.color}" stroke-width="${s.width}" stroke-linecap="round" stroke-linejoin="round" opacity="${s.kind === 'highlighter' ? .3 : 1}"/>`).join('');
    }
    function svg(input) {
        const data = normalize(input), size = bounds(data);
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${size.width}" height="${size.height}" viewBox="0 0 ${size.width} ${size.height}" role="img" aria-label="Handwriting and shapes">${paths(data)}</svg>`;
    }
    // Geometry-aware erasing: an empty point inside a rectangle does not erase its edges.
    function hit(stroke, point, radius = 10) {
        const a = stroke.points[0], b = stroke.points[stroke.points.length - 1];
        let points = stroke.points;
        if (['rectangle', 'triangle', 'ellipse'].includes(stroke.kind)) {
            const x = Math.min(a[0], b[0]), y = Math.min(a[1], b[1]), w = Math.abs(b[0] - a[0]), h = Math.abs(b[1] - a[1]);
            if (stroke.kind === 'rectangle') points = [[x,y],[x+w,y],[x+w,y+h],[x,y+h],[x,y]];
            if (stroke.kind === 'triangle') points = [[x+w/2,y],[x+w,y+h],[x,y+h],[x+w/2,y]];
            if (stroke.kind === 'ellipse') points = Array.from({length:49},(_,i)=>[x+w/2+Math.cos(i*Math.PI/24)*w/2,y+h/2+Math.sin(i*Math.PI/24)*h/2]);
        }
        const distance = (v,w) => {
            const dx=w[0]-v[0],dy=w[1]-v[1],den=dx*dx+dy*dy;
            const t=den ? Math.max(0,Math.min(1,((point[0]-v[0])*dx+(point[1]-v[1])*dy)/den)) : 0;
            return Math.hypot(point[0]-v[0]-t*dx,point[1]-v[1]-t*dy);
        };
        return points.some((p,i)=>distance(points[Math.max(0,i-1)],p)<=radius+stroke.width/2);
    }
    // Firestore rejects nested arrays. Compact point strings also reduce saved document size.
    const pack = input => { const data=normalize(input);return {version:1,height:data.height,strokes:data.strokes.map(s=>({kind:s.kind,color:s.color,width:s.width,points:JSON.stringify(s.points)}))}; };
    return {normalize, pack, simplify, path, paths, svg, bounds, hit, maxPoints, maxStrokes, maxStrokePoints, maxHeight, kinds};
}
